/**
 * Integration Engine monitoring hook (FND-009, FND-013).
 *
 * Wraps the engine's tenant-scoped observability surface:
 *   - GET /api/v1/integration/jobs          → IntegrationJob[] (run log)
 *   - GET /api/v1/integration/dead-letters   → DeadLetter[] (permanent failures)
 *
 * Both are read-only; they load together so the integration page can show the
 * job log alongside the dead-letter queue.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';
import type { DeadLetter, IntegrationJob } from '../api/types';

interface JobListResponse {
  items: IntegrationJob[];
}

interface DeadLetterListResponse {
  items: DeadLetter[];
}

interface UseIntegrationResult {
  jobs: IntegrationJob[];
  deadLetters: DeadLetter[];
  isLoading: boolean;
  error: ApiError | null;
  refresh: () => void;
}

/** Loads the tenant's integration job log + dead-letter queue. */
export function useIntegration(): UseIntegrationResult {
  const [jobs, setJobs] = useState<IntegrationJob[]>([]);
  const [deadLetters, setDeadLetters] = useState<DeadLetter[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);
    Promise.all([
      apiClient.get<JobListResponse>('/integration/jobs'),
      apiClient.get<DeadLetterListResponse>('/integration/dead-letters'),
    ])
      .then(([jobRes, dlRes]) => {
        setJobs(jobRes.items ?? []);
        setDeadLetters(dlRes.items ?? []);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setJobs([]);
        setDeadLetters([]);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { jobs, deadLetters, isLoading, error, refresh };
}
